// Internal modules
import type { DefaultInputState } from '@/store';
import { appStore } from '@/store';
import {
  features,
  FIELD_LEVEL_ERRORS,
  getContent,
  getFieldError,
  logger,
} from '@/lib';
import type { FormState } from '@/store/models/form/types';
import type { GenericObjectType, ValueOf } from '@/types';
import {
  CRS_TAX_DETAILS,
  IDENTITY_DOCUMENT_ERROR_CONTENT,
  IDENTITY_DOCUMENT_NUMBER,
  INVALID_IDENTITY_DOCUMENT_VALUE,
  SECONDARY_IDENTITY_DOCUMENT_NUMBER,
} from '../graphql/constants';

// Untyped imports
// @ts-expect-error untyped-import
import { capitalizeFirst, hasOwnProp } from '@checkout/react-components';
import { ADDRESS_TYPES } from '@/components/Address/constants';

/* === Begin Types === */
type StateType = ReturnType<typeof appStore.getState>;
type ActionsType = ReturnType<typeof appStore.getActions>;

type Errors = {
  formErrors: GenericObjectType;
  errorLogPayload: GenericObjectType;
};

type getErrorFieldAndPayloadParams = {
  addressType?: string;
  code: keyof typeof FIELD_LEVEL_ERRORS;
  formState: FormState;
  index: string | number;
};

type FieldError = {
  contentKey: string;
  field: string;
  errorCode?: string;
};

// TODO: Improve typing on this payload
type getErrorFieldAndPayloadReturnType = {
  errorFlags?: {
    hasCRSErrors?: boolean;
    isUnmappedServerError?: boolean;
    hasResidentialAddressErrors?: boolean;
  };

  field: string;
  payload: GenericObjectType;
};

/* === End Types === */

const shouldDisplayErrorOnChange = (
  id: string,
  fieldState: ValueOf<FormState> & { displayError: boolean },
  meta: GenericObjectType,
): boolean => {
  const { errors, isValid } = meta?.validation || {};

  if (isValid || !errors) {
    return false;
  }

  const { displayError } = fieldState;
  const {
    api: { allowedCardTypes },
  } = appStore.getState();
  const firstError = errors[0];

  /**
   * If the field that is being changed is the card number and we
   * don't have the allowed card types yet, we don't want to
   * display the error when the value hits the max length for certain
   * error codes (invalidType, tooLong, tooShort) because we can't
   * accurately validate those until we have the allowed card types.
   */
  if (
    id === 'cardNumber' &&
    !allowedCardTypes.length &&
    ['invalidType', 'tooShort', 'tooLong'].includes(firstError)
  ) {
    return displayError;
  }

  // Wrapping `isMaxLength` in `Boolean()` because it can be `undefined`
  // and I'd like the return value of this function to always be a boolean
  return displayError || Boolean(meta.isMaxLength);
};

/**
 * @function validateForm
 * @param {object} state
 * @returns {object} Object to merge with form state
 */
// TODO: Create explicit typing for this
const validateForm = (state: Partial<FormState>): Errors => {
  // Remove checkbox from the form validation
  // DE streamline does not require sepa to be checked
  const hideSepaTermsCheckbox = features.someActive(
    'isStreamlineOptionalExperience',
    'isStreamlineForcedExperience',
  );

  if (hideSepaTermsCheckbox) {
    delete state.sepaMandate;
  }
  const accum = { errorLogPayload: {}, formErrors: {} };
  const addressFields = /^(billing|shipping|residential)Address$/;

  const keys = Object.keys(state) as (keyof FormState)[];
  return keys.reduce((errors: Errors, field) => {
    const isAddressField = addressFields.test(field);
    const fieldState = state[field] as DefaultInputState;

    if (hasOwnProp(fieldState, 'isValid') && !fieldState.isValid) {
      const { error, errorCode, isValid, value } = fieldState;

      errors.formErrors[field] = {
        displayError: true,
        error: getFieldError({ error, errorCode, field, isValid, value }),
      };
      errors.errorLogPayload[field] = fieldState.errorCode;
    } else if (isAddressField) {
      const { formErrors, errorLogPayload } = validateForm(fieldState);
      const hasErrors = Object.keys(formErrors).length;

      if (hasErrors) {
        errors.formErrors[field] = formErrors;
        Object.entries(errorLogPayload).forEach(([fieldName, error]) => {
          const key = `${field}${capitalizeFirst(fieldName)}`;
          errors.errorLogPayload[key] = error;
        });
      }
    }

    return errors;
  }, accum);
};

// TODO: Type sumErrorFlags better
const triggerErrorBasedSpecificActions = ({
  actions,
  appState,
  sumErrorFlags = {},
  errors = [],
}: {
  actions: ActionsType;
  appState?: StateType;
  sumErrorFlags?: GenericObjectType;
  errors?: Record<string, any>;
}): void => {
  const { hasResidentialAddressErrors, hasCRSErrors, isUnmappedServerError } =
    sumErrorFlags;

  if (hasCRSErrors && isUnmappedServerError) {
    logger.info('weasley_unmapped_OAS_crs_index_error');
  }

  if (hasResidentialAddressErrors) {
    // Set if "Save" of Residential Address interstitial should submit or not
    // It'll call PayNow or PayLater handlers
    //  according to the components it's inside
    if (appState) {
      actions.form.updateResidentialAddressButtonShouldSubmit(true);
    }

    // We only open the Residential Address interstitial when
    //  the only error is in the Residential Address
    if (errors.length <= 1) {
      actions.ui.toggleIsResidentialAddressOpen(true);
    }
  }
};

const getErrorPayload = (
  field: keyof typeof IDENTITY_DOCUMENT_ERROR_CONTENT,
  index: number,
) => {
  logger.info(`INVALID_IDENTITY_DOCUMENT_VALUE_${field}_${index}`);

  return {
    displayError: true,
    error: getContent(IDENTITY_DOCUMENT_ERROR_CONTENT[field]),
    isValid: field === CRS_TAX_DETAILS ? null : false,
  };
};

/**
 * @summary: Returns error field and payload for onboardErrorHandler()
 * @description
 * Function responsible for checking if OAS is throwing errors due to
 * invalid identity documents value. We make a reference to the state,
 * because state is unique for every country and we are able to identify
 * which field we need to validate agains the identity document error.
 *
 * @examples
 * If it is RU national ID invalid, the return object will set index to 0
 * If it is RU seconday ID invalid, the return object will set index to 1
 * If any country has national id, secondary id and CRS fields, then
  the order of index value will be 0 for national id, 1 for secondary id,
  and 2 - x will be for CRS fields.
 *
 * @default: if not INVALID_IDENTITY_DOCUMENT_VALUE, default path is for
 * regular fields (eg: First, last name, address, zipcode, etc.)
 */

const getErrorFieldAndPayload = ({
  addressType,
  code,
  formState,
  index,
}: getErrorFieldAndPayloadParams): getErrorFieldAndPayloadReturnType => {
  const i = parseInt(index as string);
  // #A CRS INVALID_IDENTITY_DOCUMENT_VALUE
  if (code === INVALID_IDENTITY_DOCUMENT_VALUE) {
    // #1 CRS: IDENTITY_DOCUMENT_NUMBER
    if (formState[IDENTITY_DOCUMENT_NUMBER] && i === 0) {
      return {
        field: IDENTITY_DOCUMENT_NUMBER,
        payload: getErrorPayload(IDENTITY_DOCUMENT_NUMBER, i),
      };

      // #2  CRS: SECONDARY_IDENTITY_DOCUMENT_NUMBER
    } else if (formState[SECONDARY_IDENTITY_DOCUMENT_NUMBER] && i === 1) {
      return {
        field: SECONDARY_IDENTITY_DOCUMENT_NUMBER,
        payload: getErrorPayload(SECONDARY_IDENTITY_DOCUMENT_NUMBER, i),
      };
      // #3  CRS: CRS_TAX_DETAILS
    } else if (formState[CRS_TAX_DETAILS]) {
      if (!formState[CRS_TAX_DETAILS]?.value) {
        return {
          errorFlags: { hasCRSErrors: true },
          field: CRS_TAX_DETAILS,
          payload: { value: formState[CRS_TAX_DETAILS]?.value },
        };
      }
      const crsTaxDetails = [...formState.crsTaxDetails.value];
      /// Backend returns INVALID_IDENTITY_DOCUMENT_VALUE but we cannot determine
      /// which specific CRS field failed, so we highlight all CRS tax detail
      // fields and set the isBackendError flag to true
      // TODO: DETXOCOMPL-43 Remove this workaround once backend provides field-level error mapping.
      crsTaxDetails.forEach((taxDetail, taxDetailIndex) => {
        if (taxDetail.taxPayerIdValue.value) {
          const taxPayerIdValuePayload = getErrorPayload(
            CRS_TAX_DETAILS,
            taxDetailIndex,
          );
          taxDetail.taxPayerIdValue = {
            ...taxDetail.taxPayerIdValue,
            ...taxPayerIdValuePayload,
            isBackendError: true,
          };
        }
      });

      return {
        errorFlags: { hasCRSErrors: true, isUnmappedServerError: true },

        field: CRS_TAX_DETAILS,
        payload: { value: crsTaxDetails },
      };
    }
  }

  // #B Default path for non-crs fields
  const { contentKey, errorCode } = FIELD_LEVEL_ERRORS[code] as FieldError;
  const errorFlags: GenericObjectType = {};

  if (addressType && addressType === ADDRESS_TYPES.RESIDENTIAL_ADDRESS) {
    errorFlags.hasResidentialAddressErrors = true;
  }

  return {
    field: (FIELD_LEVEL_ERRORS[code] as FieldError)?.field,
    payload: {
      displayError: true,
      error: getContent(contentKey),
      isValid: false,
      ...(errorCode && { errorCode }),
    },
    ...(Object.keys(errorFlags).length && { errorFlags }),
  };
};

export {
  getErrorFieldAndPayload,
  shouldDisplayErrorOnChange,
  triggerErrorBasedSpecificActions,
  validateForm,
};
